import { useEffect, useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

/**
 * SystemOwnerFieldComponent
 *
 * A read-only scaffolder field that automatically resolves the owner of a
 * component by reading the `system` sibling field from the form, fetching the
 * corresponding System entity from the Backstage catalog, and surfacing its
 * `spec.owner` value.
 *
 * The resolved owner is written back via `onChange` so that subsequent steps
 * can reference `parameters.owner`.
 *
 * Usage in template.yaml:
 *   owner:
 *     title: Owner
 *     type: string
 *     ui:field: SystemOwnerField
 */
const SystemOwnerFieldComponent = ({
  onChange,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const catalogApi = useApi(catalogApiRef);

  const [owner, setOwner] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read the `system` field value from sibling form data
  const systemRef: string | undefined = formContext?.formData?.system;

  useEffect(() => {
    if (!systemRef) {
      setOwner('');
      setError(null);
      onChange(undefined as any);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        // Normalise the entity ref — EntityPicker emits fully-qualified refs
        // like "system:default/my-system"; parseEntityRef handles all forms.
        const ref = parseEntityRef(systemRef, { defaultKind: 'System', defaultNamespace: 'default' });
        const entity = await catalogApi.getEntityByRef(stringifyEntityRef(ref));

        if (cancelled) return;

        if (!entity) {
          setError(`System '${systemRef}' not found in catalog`);
          setOwner('');
          onChange(undefined as any);
          return;
        }

        const rawOwner = (entity.spec?.owner as string) ?? '';
        // Strip the group:default/ prefix for a clean display value
        const displayOwner = rawOwner.replace(/^group:default\//, '').replace(/^group:/, '');
        setOwner(displayOwner);
        onChange(displayOwner);
      } catch (e: any) {
        if (!cancelled) {
          setError(`Could not resolve owner: ${e?.message ?? e}`);
          setOwner('');
          onChange(undefined as any);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [systemRef]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TextField
      label="Owner"
      value={loading ? '' : (owner || (error ? '' : '— select a System above —'))}
      InputProps={{
        readOnly: true,
        startAdornment: loading ? (
          <InputAdornment position="start">
            <CircularProgress size={16} />
          </InputAdornment>
        ) : undefined,
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title="Automatically inherited from the selected System's owner. Change the System selection to update this field.">
              <InfoOutlinedIcon fontSize="small" color="disabled" />
            </Tooltip>
          </InputAdornment>
        ),
      }}
      helperText={
        error ??
        (owner
          ? `Inherited from system: ${systemRef}`
          : 'Select a System above to resolve the owner')
      }
      error={!!error}
      fullWidth
      variant="outlined"
    />
  );
};

export const SystemOwnerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'SystemOwnerField',
    component: SystemOwnerFieldComponent,
  }),
);
